import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

const categoryTabs = [
  { key: 'all', label: 'categories.all' },
  { key: 'beauty', label: 'categories.beauty' },
  { key: 'hair', label: 'categories.hair' },
  { key: 'nail', label: 'categories.nail' },
  { key: 'body', label: 'categories.body' },
];

const PAGE_SIZE = 6;

export default function Articles() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return articles;
    return articles.filter((a) => a.category === activeTab);
  }, [activeTab]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setPage(1);
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '美业资讯 - 91美',
            description: '91美美业资讯，获取最新美容、美发、美甲、美体行业动态与护理知识。',
            url: 'https://91mei.com/articles',
            isPartOf: {
              '@type': 'WebSite',
              name: '91美',
              url: 'https://91mei.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91mei.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '美业资讯',
                item: 'https://91mei.com/articles',
              },
            ],
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb
            items={[{ label: t('breadcrumb.articles') }]}
          />

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-8">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {t('articles.title')}
                </h1>
                <p className="text-sm text-foreground-500">
                  {t('articles.subtitle')}
                </p>
              </div>

              <div className="flex items-center justify-center mb-10">
                <div className="inline-flex bg-background-100 rounded-full p-1 flex-wrap justify-center">
                  {categoryTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                        activeTab === tab.key
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {t(tab.label)}
                    </button>
                  ))}
                </div>
              </div>

              {paged.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-article-line text-4xl text-foreground-300 mb-3 block"></i>
                  <p className="text-foreground-500 text-sm">暂无相关文章</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {paged.map((article) => (
                      <ArticleCard
                        key={article.id}
                        id={article.id}
                        title={article.title}
                        summary={article.summary}
                        author={article.author}
                        publishDate={article.publishDate}
                        readCount={article.readCount}
                        image={article.image}
                        category={
                          article.category === 'beauty'
                            ? '美容'
                            : article.category === 'hair'
                            ? '美发'
                            : article.category === 'nail'
                            ? '美甲'
                            : '美体'
                        }
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setPage(i + 1)}
                          className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-all cursor-pointer ${
                            page === i + 1
                              ? 'bg-primary-500 text-white'
                              : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}